import type { SearchHookLocation } from "@repo/maps";

export type AgentSortOption =
  | "recommended"
  | "ai_score"
  | "most_active"
  | "highest_rated"
  | "most_experienced"
  | "most_reviews";

export type AgentFilters = {
  location: SearchHookLocation | null;
  name: string | null;
  languageIds: number[];
  specialtyIds: number[];
  sort: AgentSortOption;
  page: number;
};

export const AGENT_SEARCH_URL_KEYS = {
  name: "name",
  language: "lang",
  specialty: "spec",
  sort: "sort",
  page: "page",
};

const VALID_SORTS: AgentSortOption[] = [
  "recommended",
  "ai_score",
  "most_active",
  "highest_rated",
  "most_experienced",
  "most_reviews",
];

export function getAgentSearchParams(
  filters: Omit<AgentFilters, "location">,
  initialParams?: URLSearchParams,
) {
  const params = initialParams ?? new URLSearchParams();

  if (filters.name) {
    params.set(AGENT_SEARCH_URL_KEYS.name, filters.name);
  } else {
    params.delete(AGENT_SEARCH_URL_KEYS.name);
  }

  if (filters.languageIds.length > 0) {
    params.set(AGENT_SEARCH_URL_KEYS.language, filters.languageIds.join(","));
  } else {
    params.delete(AGENT_SEARCH_URL_KEYS.language);
  }

  if (filters.specialtyIds.length > 0) {
    params.set(AGENT_SEARCH_URL_KEYS.specialty, filters.specialtyIds.join(","));
  } else {
    params.delete(AGENT_SEARCH_URL_KEYS.specialty);
  }

  if (filters.sort && filters.sort !== "recommended") {
    params.set(AGENT_SEARCH_URL_KEYS.sort, filters.sort);
  } else {
    params.delete(AGENT_SEARCH_URL_KEYS.sort);
  }

  if (filters.page && filters.page > 1) {
    params.set(AGENT_SEARCH_URL_KEYS.page, filters.page.toString());
  } else {
    params.delete(AGENT_SEARCH_URL_KEYS.page);
  }

  return params;
}

export function updateAgentSearchParams(
  pathname: string,
  params: URLSearchParams,
  filters: AgentFilters,
) {
  const prevStr = params.toString();
  const newParams = getAgentSearchParams(filters, params);
  const newStr = newParams.toString();

  if (prevStr === newStr) {
    return;
  }

  window.history.replaceState({}, "", `${pathname}?${newParams.toString()}`);
}

export function readFromAgentSearchParams(
  params: URLSearchParams,
): Omit<AgentFilters, "location"> {
  const name = params.get(AGENT_SEARCH_URL_KEYS.name);
  const languageIds = params.get(AGENT_SEARCH_URL_KEYS.language);
  const specialtyIds = params.get(AGENT_SEARCH_URL_KEYS.specialty);
  const sortParam = params.get(AGENT_SEARCH_URL_KEYS.sort);
  const page = params.get(AGENT_SEARCH_URL_KEYS.page);

  const sort: AgentSortOption =
    sortParam && VALID_SORTS.includes(sortParam as AgentSortOption)
      ? (sortParam as AgentSortOption)
      : "recommended";

  return {
    // URLSearchParams already decodes values, including literal percent signs.
    name: name || null,
    languageIds: languageIds ? languageIds.split(",").map(Number) : [],
    specialtyIds: specialtyIds ? specialtyIds.split(",").map(Number) : [],
    sort,
    page: page ? Number.parseInt(page, 10) : 1,
  };
}
